import { fetchItemsAPI } from "./itemsApi";
import type { ArchivedListMode } from "@/utils/itemArchive";
import { fetchComponentsFromAPI, invalidateComponentsListCache } from "./componentsApi";
import { resolveCollectionIdentifier } from "../utils/collectionUtils";
import { buildCollectionTableKey, normalizeOrgSegment } from "../utils/collectionPaths";
import {
    isOrganizationScopingDisabled,
    organizationForApi,
    pickComponentForSlugWhenScopingDisabled,
} from "../utils/organizationScoping";
import { getSystemRelationSource } from "../utils/systemRelationSources";

let componentsCache: { components: any[]; timestamp: number; userKey: string } | null = null;
const COMPONENTS_CACHE_TTL = 120000; // 2 minutes

function componentsListUserKey(user: any): string {
    if (!user || typeof user !== 'object') return '';
    return String(
        user.uid ||
        user.info?.uid ||
        user.id ||
        user._id ||
        user.info?.id ||
        user.info?._id ||
        ''
    ).trim();
}

/** Bust client caches so the next table load sees newly created collections. */
export function invalidateAllCachesUsedByFetchMeta(): void {
    invalidateComponentsListCache();
    componentsCache = null;
}

// Ownership-based helpers removed (deprecated)

const fetchItems = async (
    componentMeta: any,
    user: any,
    pagination?: { page?: number; limit?: number },
    filters?: Record<string, string[]>,
    archived?: ArchivedListMode,
    sort?: { orderBy?: string; orderDirection?: 'asc' | 'desc' },
    columnFilters?: Record<string, unknown>
) => {
    return new Promise<any>(async (resolve, reject) => {
        // Declare collection outside try block so it's accessible in catch block
        let collection: string | undefined;

        try {
            if (!componentMeta) {
                console.error('Component meta is undefined');
                reject('Component meta is undefined');
                return;
            }

            // Use resolveCollectionIdentifier to get the collection, which will fallback to slug if needed
            collection = resolveCollectionIdentifier(componentMeta, componentMeta.slug);

            console.log('🔍 fetchItems: Collection resolution:', {
                slug: componentMeta.slug,
                name: componentMeta.name,
                dataSource: componentMeta.dataSource,
                mongoCollection: (componentMeta as any).mongoCollection,
                collection: componentMeta.collection,
                resolvedCollection: collection,
                fallbackSlug: componentMeta.slug
            });

            // Warn if collection doesn't match slug (potential misconfiguration)
            if (collection && collection !== componentMeta.slug && !collection.includes(componentMeta.slug)) {
                console.warn(`⚠️ fetchItems: Collection "${collection}" doesn't match slug "${componentMeta.slug}" - this may indicate a misconfiguration`);
            }

            if (!collection) {
                const errorDetails = {
                    slug: componentMeta.slug,
                    name: componentMeta.name,
                    dataSource: componentMeta.dataSource,
                    mongoCollection: (componentMeta as any).mongoCollection,
                    collection: componentMeta.collection
                };
                const errorMsg = `Collection is undefined for component "${componentMeta.name || componentMeta.slug}". Please configure collection or mongoCollection in the admin panel.`;
                console.error('Collection is undefined for meta:', errorDetails);
                reject(errorMsg);
                return;
            }

            // Use slug instead of collection name - backend uses slug to find component, then resolves collection
            const slug = componentMeta.slug;
            const apiOptions: {
                page?: number;
                limit?: number;
                orderBy?: string;
                orderDirection?: 'asc' | 'desc';
                filters?: Record<string, string[]>;
                columnFilters?: Record<string, unknown>;
                organization?: string;
                archived?: ArchivedListMode;
            } = {};
            if (pagination?.page != null) apiOptions.page = pagination.page;
            if (pagination?.limit != null) apiOptions.limit = pagination.limit;
            if (sort?.orderBy) apiOptions.orderBy = sort.orderBy;
            if (sort?.orderDirection) apiOptions.orderDirection = sort.orderDirection;
            if (filters && Object.keys(filters).length > 0) apiOptions.filters = filters;
            if (columnFilters && Object.keys(columnFilters).length > 0) {
                apiOptions.columnFilters = columnFilters;
            }
            if (archived && archived !== "no") {
                apiOptions.archived = archived;
            }
            const orgForApi = organizationForApi(componentMeta.organization);
            if (orgForApi) {
                apiOptions.organization = orgForApi;
            }
            console.log(`📋 fetchItems: Fetching items using slug "${slug}" (collection: "${collection}") for component "${componentMeta.slug}"`, apiOptions);
            const result = await fetchItemsAPI(slug, apiOptions);
            const items = result.items ?? [];
            console.log(`✅ fetchItems: Received ${items?.length || 0} items using slug "${slug}"`, result.totalCount != null ? `totalCount: ${result.totalCount}` : '');

            // Ensure slug is sanitized before returning
            const sanitizedSlug = typeof componentMeta.slug === 'string' ? componentMeta.slug.replace(/[!]+$/, '').trim() : componentMeta.slug;
            const tableKey = componentMeta.tableKey || buildCollectionTableKey(sanitizedSlug, componentMeta.organization);
            const metaOut = { ...componentMeta, slug: sanitizedSlug, tableKey };
            resolve({
                items,
                totalCount: result.totalCount,
                slug: tableKey,
                meta: metaOut
            });

        } catch (error: any) {
            console.error('Failed to fetch items via API:', error);

            // Check if this is a 404 error for a mongo-based collection
            const is404 = error?.status === 404 || error?.message?.includes('404') || error?.message?.includes('HTTP 404');
            const isMongoCollection = componentMeta?.dataSource === 'mongodb';

            if (is404 && isMongoCollection && componentMeta) {
                // Resolve collection if not already resolved (in case error happened before collection was set)
                const resolvedCollection = collection || resolveCollectionIdentifier(componentMeta, componentMeta.slug);

                // Create a special error object with metadata for 404 on mongo collections
                const errorObj: any = {
                    message: error?.message || 'Collection not found',
                    status: 404,
                    type: 'COLLECTION_NOT_FOUND_404',
                    isMongoCollection: true,
                    collection: resolvedCollection,
                    componentMeta: componentMeta,
                    slug: componentMeta.slug
                };
                reject(errorObj);
                return;
            }

            reject(error);
        }
    });
}

export type ResolveComponentBySlugOptions = {
    /** URL / API org segment when slug is scoped */
    organization?: string;
};

/**
 * Resolve component configuration by slug without loading collection items.
 * Used for relation-field metadata; table loads should use {@link fetchMeta} instead.
 */
export async function resolveComponentBySlug(
    slugName: unknown,
    user: unknown,
    options?: ResolveComponentBySlugOptions
): Promise<Record<string, unknown>> {
    const sanitizedSlugName =
        typeof slugName === 'string' ? slugName.replace(/[!]+$/, '').trim() : slugName;

    const listUserKey = componentsListUserKey(user);
    let components: any[];
    const now = Date.now();
    if (
        componentsCache &&
        now - componentsCache.timestamp < COMPONENTS_CACHE_TTL &&
        componentsCache.userKey === listUserKey
    ) {
        components = componentsCache.components;
    } else {
        components = await fetchComponentsFromAPI({ userUid: listUserKey || undefined });
        componentsCache = {
            components,
            timestamp: now,
            userKey: listUserKey
        };
    }

    const orgQueryNorm = organizationForApi(options?.organization)
        ? normalizeOrgSegment(options?.organization)
        : '';

    const slugEquals = (comp: any) => {
        const compSlugSanitized =
            typeof comp.slug === 'string' ? comp.slug.replace(/[!]+$/, '').trim() : comp.slug;
        return (
            compSlugSanitized === sanitizedSlugName ||
            comp.slug === sanitizedSlugName ||
            compSlugSanitized === slugName ||
            comp.slug === slugName
        );
    };

    const slugMatches = components.filter(slugEquals);

    let component: any = null;
    if (slugMatches.length > 0) {
        if (isOrganizationScopingDisabled()) {
            component = pickComponentForSlugWhenScopingDisabled(slugMatches);
        } else if (orgQueryNorm) {
            component =
                slugMatches.find((comp: any) => normalizeOrgSegment(comp.organization) === orgQueryNorm) ||
                null;
        } else {
            component = slugMatches.find((comp: any) => !normalizeOrgSegment(comp.organization)) || null;
        }
        if (!component && slugMatches.length === 1) {
            component = slugMatches[0];
        }
    }

    if (slugMatches.length > 0 && !component) {
        throw {
            message: `Multiple collections use the slug "${sanitizedSlugName}". Open it from the Collections menu so the correct workspace is selected.`,
            slug: sanitizedSlugName,
            originalSlug: slugName !== sanitizedSlugName ? slugName : undefined,
            type: 'COLLECTION_SLUG_AMBIGUOUS' as const
        };
    }

    if (!component) {
        component =
            components.find((comp: any) => {
                return (
                    comp.collection === sanitizedSlugName ||
                    (comp as any).mongoCollection === sanitizedSlugName ||
                    comp.collection === slugName ||
                    (comp as any).mongoCollection === slugName
                );
            }) || null;
    }

    if (!component) {
        const availableSlugs = components
            .map((c: any) => {
                const slug = c.slug;
                return typeof slug === 'string' ? slug.replace(/[!]+$/, '').trim() : slug;
            })
            .filter(Boolean);
        const displaySlug = sanitizedSlugName;
        const errorMsg =
            availableSlugs.length === 0
                ? `The collection "${displaySlug}" was not found.`
                : `Collection "${displaySlug}" is not in your accessible list.`;
        throw {
            message: errorMsg,
            slug: displaySlug,
            originalSlug: slugName !== sanitizedSlugName ? slugName : undefined,
            availableSlugs: availableSlugs.length > 0 ? availableSlugs : undefined,
            type: 'COMPONENT_NOT_FOUND' as const
        };
    }

    const compSlugSanitized =
        typeof component.slug === 'string' ? component.slug.replace(/[!]+$/, '').trim() : component.slug;
    const scopedTableKey = buildCollectionTableKey(compSlugSanitized, component.organization);
    return { ...component, slug: compSlugSanitized, tableKey: scopedTableKey };
}

export const fetchMeta = async (
    slugName: any,
    user: any,
    options?: {
        pagination?: { page?: number; limit?: number };
        filters?: Record<string, string[]>;
        columnFilters?: Record<string, unknown>;
        sort?: { orderBy?: string; orderDirection?: 'asc' | 'desc' };
        /** URL / API org segment when slug is scoped */
        organization?: string;
        /** List archived items: default omits archived; `all` / `only` passed to items API */
        archived?: ArchivedListMode;
    }
) => {
    return new Promise<any>(async (resolve, reject) => {
        try {
            const sanitizedSlugName = typeof slugName === 'string' ? slugName.replace(/[!]+$/, '').trim() : slugName;
            console.log(`🔍 fetchMeta: Looking up component for slug "${slugName}" (sanitized: "${sanitizedSlugName}")`);

            const componentScoped = await resolveComponentBySlug(slugName, user, {
                organization: options?.organization,
            });

            console.log(`✅ fetchMeta: Found component "${componentScoped.name}" with slug "${componentScoped.slug}"`);
            const resolvedCollection = resolveCollectionIdentifier(componentScoped, componentScoped.slug as string);
            const hasExplicitCollection = Boolean(
                (componentScoped as any).mongoCollection || componentScoped.collection
            );
            if (
                resolvedCollection &&
                resolvedCollection !== componentScoped.slug &&
                !hasExplicitCollection
            ) {
                console.warn(
                    `⚠️ fetchMeta: Component "${componentScoped.name}" (slug: "${componentScoped.slug}") resolves to collection "${resolvedCollection}" instead of slug - check component configuration`
                );
            }

            const result = await fetchItems(
                componentScoped,
                user,
                options?.pagination,
                options?.filters,
                options?.archived,
                options?.sort,
                options?.columnFilters
            );
            resolve(result);

        } catch (error: any) {
            const errorMessage = error?.message || error?.toString() || 'Unknown error';
            console.error('fetchMeta failed:', errorMessage);

            if (error?.type === 'COLLECTION_NOT_FOUND_404') {
                reject(error);
                return;
            }

            if (error && typeof error === 'object') {
                reject(error);
                return;
            }

            reject({ message: errorMessage || 'Failed to fetch meta' });
        }
    });
}

export function fetchCollection(params: any) {
    return new Promise<any>((resolve, reject) => {
        const options: {
            pagination?: { page?: number; limit?: number };
            filters?: Record<string, string[]>;
            columnFilters?: Record<string, unknown>;
            sort?: { orderBy?: string; orderDirection?: 'asc' | 'desc' };
            organization?: string;
            archived?: ArchivedListMode;
        } = {};
        if (params?.pagination) options.pagination = params.pagination;
        if (params?.sort) options.sort = params.sort;
        if (params?.filters && typeof params.filters === 'object') options.filters = params.filters;
        if (params?.columnFilters && typeof params.columnFilters === 'object') {
            options.columnFilters = params.columnFilters;
        }
        if (params?.organization && String(params.organization).trim()) {
            options.organization = String(params.organization).trim();
        }
        const ar = params?.archived;
        if (ar === "all" || ar === "only" || ar === "no") {
            options.archived = ar;
        }
        fetchMeta(params.slugName, params?.user, options).then((res: any) => {
            // Use the meta from the result instead of global meta to avoid race conditions
            const componentMeta = res.meta;
            const page = params?.pagination?.page;
            const pageSize = params?.pagination?.limit;
            const requestTableKey =
                typeof params?.tableKey === 'string' && params.tableKey.trim()
                    ? params.tableKey.trim()
                    : undefined;
            resolve({
                slug: requestTableKey || res.slug,
                meta: componentMeta,
                data: res.items || [],
                pagination: (page != null || pageSize != null || res.totalCount != null)
                    ? { page: page ?? 1, pageSize: pageSize ?? 25, totalCount: res.totalCount }
                    : undefined
            });
        }).catch((error: any) => {
            console.error('fetchCollection failed:', error?.message || String(error));
            reject(error);
        });
    })
}

export function fetchRelatedItems(componentMeta?: any) {
    return new Promise<any>(async (resolve, reject) => {
        try {
            // Accept componentMeta as parameter to avoid using global state
            // If not provided, this function cannot work (but maintain backward compatibility)
            if (!componentMeta || !componentMeta.fields) {
                console.warn('fetchRelatedItems: No component meta provided, returning empty array');
                resolve([]);
                return;
            }

            // Find relation fields
            const relationFields = componentMeta.fields.filter((field: any) =>
                field.type === "relation" || field.type === "multiplerelation" || field.type === "systemrelation"
            );

            if (relationFields.length === 0) {
                resolve([]);
                return;
            }

            // Fetch related items for each relation field via API
            const relatedItemsPromises = relationFields.map(async (field: any) => {
                try {
                    if (field.type === "systemrelation") {
                        const systemSource = getSystemRelationSource(field.value);
                        if (!systemSource) {
                            return { field: field.value, items: [] };
                        }
                        const relatedItems = await systemSource.fetchItems();
                        return {
                            field: field.value,
                            items: relatedItems
                        };
                    }
                    // Extract slug from collection name if it contains underscore (e.g., "rooms_brdxlolm3" -> "rooms")
                    // Backend uses slug to find component, not collection name
                    let slugToUse = field.value;
                    if (typeof field.value === 'string' && field.value.includes('_')) {
                        // Try to find component by collection name first, but also extract slug part
                        const slugPart = field.value.split('_')[0];
                        console.log(`🔗 fetchRelatedItems: Field value "${field.value}" contains underscore, using slug part "${slugPart}"`);
                        slugToUse = slugPart;
                    }
                    const orgForApi = normalizeOrgSegment(componentMeta.organization);
                    const result = await fetchItemsAPI(slugToUse, {
                        aclAction: 'relate',
                        ...(orgForApi ? { organization: orgForApi } : {}),
                    });
                    return {
                        field: field.value,
                        items: result.items ?? []
                    };
                } catch (error) {
                    console.error(`Failed to fetch related items for field ${field.value}:`, error);
                    return {
                        field: field.value,
                        items: []
                    };
                }
            });

            const relatedItemsResults = await Promise.all(relatedItemsPromises);
            resolve(relatedItemsResults);

        } catch (error) {
            console.error('Failed to fetch related items:', error);
            reject(error);
        }
    });
}